import * as sdk from 'livekit-client';
import { toDeviceId } from '@studio/models/device';
import {
  beginVideoTrackWait,
  clearVideoTrackWait,
  failVideoTrackWait,
  getVideoTrackWaitState,
  type VideoTrackWaitState
} from './track-wait.js';

type Observation = {
  deviceId: string;
  codec: string;
  rejected: boolean;
  failedState: VideoTrackWaitState | undefined;
  dispose: () => void;
};

/** Keep explicit format rejections with their publication, rather than a reusable track SID. */
export class VideoSubscriptionFailures {
  #rooms = new Map<sdk.Room, Map<sdk.RemoteTrackPublication, Observation>>();
  #automaticWaits = new WeakSet<VideoTrackWaitState>();

  canSubscribe(
    room: sdk.Room,
    participant: sdk.RemoteParticipant,
    publication: sdk.RemoteTrackPublication,
    ownsRoom: () => boolean
  ): boolean {
    if (publication.kind !== sdk.Track.Kind.Video) return true;
    if (
      !ownsRoom() ||
      ![...room.remoteParticipants.values()].includes(participant) ||
      ![...participant.videoTrackPublications.values()].includes(publication)
    )
      return false;
    let publications = this.#rooms.get(room);
    if (!publications) {
      publications = new Map();
      this.#rooms.set(room, publications);
    }
    let observation = publications.get(publication);
    const deviceId = toDeviceId(participant.identity);
    if (!observation) {
      // Unpublished streams must release their listeners even if the room stays open.
      const livePublications = new Set(
        [...room.remoteParticipants.values()].flatMap((publisher) => [
          ...publisher.videoTrackPublications.values()
        ])
      );
      for (const [previous, entry] of publications) {
        if (!livePublications.has(previous)) {
          entry.dispose();
          publications.delete(previous);
        }
      }
      const entry: Observation = {
        deviceId,
        codec: publication.mimeType ?? '',
        rejected: false,
        failedState: undefined,
        dispose: () => publication.off?.(sdk.TrackEvent.SubscriptionFailed, onFailure)
      };
      const onFailure = (reason?: sdk.SubscriptionError): void => {
        // A retired room/participant/publication may finish an old callback after replacement.
        if (
          reason !== sdk.SubscriptionError.SE_CODEC_UNSUPPORTED ||
          !ownsRoom() ||
          ![...room.remoteParticipants.values()].includes(participant) ||
          ![...participant.videoTrackPublications.values()].includes(publication)
        )
          return;
        entry.codec = publication.mimeType ?? '';
        entry.rejected = true;
        // Per settle-every-terminal-branch (global): settle the pending wait immediately.
        if (publication.isDesired) failVideoTrackWait(deviceId, 'unsupported-video-format');
        entry.failedState = getVideoTrackWaitState(deviceId);
      };
      // Listen on the publication itself: Room's error event supplies only a SID,
      // which cannot distinguish a late error from a replacement using that SID.
      publication.on?.(sdk.TrackEvent.SubscriptionFailed, onFailure);
      publications.set(publication, entry);
      observation = entry;
    }

    const wait = getVideoTrackWaitState(deviceId);
    const explicitRetry =
      wait?.phase === 'waiting' &&
      wait !== observation.failedState &&
      !this.#automaticWaits.has(wait);
    if (
      observation.rejected &&
      observation.codec === (publication.mimeType ?? '') &&
      !explicitRetry
    )
      return false;

    observation.rejected = false;
    // A new publication, room, changed codec, or explicit source retry gets a fresh wait.
    if (wait?.phase === 'failed' && wait.cause === 'unsupported-video-format') {
      beginVideoTrackWait(deviceId);
      const automaticWait = getVideoTrackWaitState(deviceId);
      if (automaticWait) this.#automaticWaits.add(automaticWait);
    }
    return true;
  }

  isRejected(publication: sdk.RemoteTrackPublication): boolean {
    for (const publications of this.#rooms.values()) {
      const observation = publications.get(publication);
      if (observation?.rejected && observation.codec === (publication.mimeType ?? '')) return true;
    }
    return false;
  }

  /** Retain allowlisted rejection facts even after the SDK removes the unusable media track. */
  diagnostic(deviceId: string): Array<{ codec: string; error: 'codec-unsupported' }> {
    const failures: Array<{ codec: string; error: 'codec-unsupported' }> = [];
    for (const [room, publications] of this.#rooms) {
      for (const participant of room.remoteParticipants.values()) {
        if (participant.identity !== deviceId) continue;
        for (const publication of participant.videoTrackPublications.values()) {
          const observation = publications.get(publication);
          if (observation?.rejected)
            failures.push({ codec: observation.codec, error: 'codec-unsupported' });
        }
      }
    }
    return failures;
  }

  forgetRoom(room: sdk.Room): void {
    const publications = this.#rooms.get(room);
    this.#rooms.delete(room);
    for (const observation of publications?.values() ?? []) {
      observation.dispose();
      // Do not erase a newer attempt's status while disposing the old room.
      if (observation.failedState === getVideoTrackWaitState(observation.deviceId)) {
        clearVideoTrackWait(observation.deviceId);
      }
    }
  }
}
